import { useState } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTeams, useCurrentTeam } from '@/hooks/useTeams';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface TeamSetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTeamCreated?: (newTeamId?: string) => void;
}

export const TeamSetupModal = ({ open, onOpenChange, onTeamCreated }: TeamSetupModalProps) => {
  const { user } = useAuth();
  const { refetch } = useTeams();
  const { setCurrentTeamId } = useCurrentTeam();
  const { toast } = useToast();
  const [teamName, setTeamName] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<'creating' | 'switching' | 'finalizing'>('creating');

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim() || !user) return;

    setLoading(true);
    setCurrentStep('creating');
    
    try {
      // Create the team with a predefined id to avoid RLS select-on-insert issues
      const newTeamId = crypto.randomUUID() as `${string}-${string}-${string}-${string}-${string}`;

      const { error: teamError } = await supabase
        .from('teams')
        .insert({
          id: newTeamId,
          name: teamName.trim(),
          company_name: teamName.trim(),
        });

      if (teamError) throw teamError;

      // Add user as team owner
      const { error: membershipError } = await supabase
        .from('team_memberships')
        .insert({
          team_id: newTeamId,
          user_id: user.id,
          role: 'owner'
        });

      if (membershipError) throw membershipError;

      setCurrentStep('switching');
      
      // Refresh teams data
      await refetch();
      
      setCurrentStep('finalizing');
      
      // Switch to the new team
      setCurrentTeamId(newTeamId);
      
      // Update user profile with new team
      await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          last_selected_team_id: newTeamId
        }, {
          onConflict: 'user_id'
        });
      
      // Show success message
      toast({
        title: 'Team created successfully!',
        description: `Welcome to ${teamName.trim()}. You can now start adding businesses.`,
      });
      
      // Call the callback and close modal
      onTeamCreated?.(newTeamId);
      onOpenChange(false);
      
      // Reset form and state
      setTeamName('');
      setCurrentStep('creating');
    } catch (error) {
      console.error('Error creating team:', error);
      toast({
        title: 'Error creating team',
        description: 'There was a problem creating your team. Please try again.',
        variant: 'destructive'
      });
      setCurrentStep('creating');
    } finally {
      setLoading(false);
    }
  };

  const getLoadingText = () => {
    switch (currentStep) {
      case 'creating':
        return 'Creating team...';
      case 'switching':
        return 'Setting up workspace...';
      case 'finalizing':
        return 'Finalizing setup...';
      default:
        return 'Creating...';
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-card border border-border" style={{
        boxShadow: '0px 10px 6px rgba(0, 0, 0, 0.06), 0px 0px 0px 8px rgba(0, 0, 0, 0.03), 0px 1px 2px rgba(0, 0, 0, 0.05)'
      }}>
        <DialogHeader>
          <div className="flex justify-start mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.9995 6.16489L0.00012207 9.08278V6.36043C0.00012207 4.53312 1.25045 2.94267 3.02542 2.51167L11.9995 0.329102V6.16489Z" fill="black" />
              <path d="M3.02542 15.6535L11.9995 17.836V12.0002L0.00012207 9.08234V11.8047C0.00012207 13.632 1.25045 15.2224 3.02542 15.6535Z" fill="black" />
              <path d="M12.0035 17.8347L24.0027 14.9169V17.6392C24.0027 19.4665 22.7525 21.057 20.9775 21.488L12.0035 23.6706V17.8347Z" fill="black" />
              <path d="M20.9775 8.34577L12.0035 6.16321V11.9989L24.0028 14.9169V12.1946C24.0028 10.3673 22.7525 8.77678 20.9775 8.34577Z" fill="black" />
            </svg>
          </div>
          <DialogTitle className="text-xl font-semibold text-foreground leading-none text-left">
            Create your first team
          </DialogTitle>
          <p className="text-base text-muted-foreground leading-6 text-left">
            Let's set up your team to start serving small business owners better, together.
          </p>
        </DialogHeader>

        <div className="flex flex-col gap-4 mt-6">
          <form onSubmit={handleCreateTeam} className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="teamName" className="text-sm font-medium text-foreground">
                Team Name
              </Label>
              <Input
                id="teamName"
                type="text"
                placeholder="Enter your team or company name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                required
                className="h-9 px-3 text-sm bg-card border border-border rounded-lg shadow-sm focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading || !teamName.trim()} 
              className="w-full h-9 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#FDFDFD] text-sm font-medium rounded-lg shadow-lg border-none" 
              style={{
                boxShadow: '0px 0px 0px 1px #1A1A1A, 0px 4px 6px -1px rgba(0, 0, 0, 0.2), 0px 2px 4px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {getLoadingText()}
                </div>
              ) : (
                "Create Team"
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};